import { BEIGE_600, GRAY_800, WHITE } from 'constants/styling/theme'

import Box from '@mui/material/Box'
import { FC } from 'react'
import { ProductKind } from 'constants/product'
import { getProductKindIcon } from 'utils/helpers/getProductKindIcon'

/** Chip and glyph sizes; the asset glyphs carry their own inner margin, so they fill most of the chip */
const SIZES = {
  md: { chip: '2.4rem', glyph: '2rem' },
  lg: { chip: '3.2rem', glyph: '2.8rem' },
}

/**
 * @interface Props Input properties
 */
export interface Props {
  /** The product kind whose glyph it draws (`getProductKindIcon`) */
  kind: ProductKind
  /** Chip diameter: `md` 2.4rem (default), `lg` 3.2rem */
  size?: 'md' | 'lg'
  /** Chip colour: `beige` (default), or `white` for the selected state of the card holding it */
  background?: 'white' | 'beige'
  /** Decorative by default: the text next to it names the product */
  'aria-hidden'?: boolean
}

/**
 * @component A round chip carrying a product's glyph, e.g. in front of a kind of content.
 * @example
 * <ProductIcon kind={ProductKind.DRONE_PHOTO} size="lg" background="white" />
 */
export const ProductIcon: FC<Props> = ({
  kind,
  size = 'md',
  background = 'beige',
  'aria-hidden': ariaHidden = true,
}) => (
  <Box
    component="span"
    aria-hidden={ariaHidden || undefined}
    sx={{
      display: 'inline-flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      width: SIZES[size].chip,
      height: SIZES[size].chip,
      borderRadius: '50%',
      backgroundColor: background === 'white' ? WHITE : BEIGE_600,
      color: GRAY_800,
      '& svg': { display: 'block', width: SIZES[size].glyph, height: SIZES[size].glyph },
      // the asset glyphs are drawn in black strokes
      '& svg [stroke]': { stroke: 'currentColor' },
    }}
  >
    {getProductKindIcon(kind)}
  </Box>
)
